import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { ExtractParams } from '@/types/params';

import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import { PMPRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';

// Dummy data for demonstration
const dummyGeneralKnowledge = `
• The property has a total of 24 units across 3 buildings
• Maintenance requests are typically handled within 24-48 hours
• Rent is due on the 1st of each month with a 5-day grace period
• Parking is available in the underground garage for $150/month
• Laundry facilities are located in Building A and Building C
• Trash collection occurs every Monday and Thursday
• The property has a 24/7 security system with key fob access
• Common areas are cleaned daily by our maintenance staff
• Pet policy allows up to 2 pets with a $300 deposit per pet
• Smoking is prohibited in all indoor areas
`;

const dummyTopicKnowledge = {
  maintenance: `
• HVAC systems are serviced quarterly
• Emergency maintenance is available 24/7
• Regular maintenance hours are 8 AM - 5 PM Monday through Friday
• Maintenance requests can be submitted through the tenant portal
• Common maintenance issues include:
  - Clogged drains
  - HVAC filter replacements
  - Light bulb replacements
  - Lock issues
• Preventative maintenance is performed monthly
• Maintenance staff contact: (555) 123-4567
• Emergency maintenance contact: (555) 987-6543
`,
  rent: `
• Rent is due on the 1st of each month
• 5-day grace period applies
• Late fees are $50 after the 5th
• Additional $10 per day after the 10th
• Payment methods accepted:
  - Online portal
  - Bank transfer
  - Money order
  - Cashier's check
• Rent increases are limited to once per year
• 60-day notice required for rent increases
• Security deposit is equal to one month's rent
`,
  parking: `
• Underground parking available for $150/month
• Surface parking available for $100/month
• Visitor parking is free for up to 24 hours
• Parking permits must be displayed at all times
• Towing enforced for unauthorized vehicles
• Reserved parking spots available for an additional fee
• Parking garage hours: 24/7
• Parking office hours: 9 AM - 5 PM Monday through Friday
• Parking contact: (555) 234-5678
`,
};

const formatDate = (date: Date) => {
  const [dateOnly] = date.toISOString().split('T');
  return dateOnly;
};

const getDefaultStartDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 7);
  return formatDate(date);
};

const addDays = (date: string, days: number) => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return formatDate(newDate);
};

const PropertyLocationViewKnowledgeSubpage: React.FC = () => {
  const { session } = useAuth();
  const { id } = useParams<ExtractParams<PMPRoutes.propertyLocationViewKnowledge>>();

  // General knowledge dates
  const [startDate, setStartDate] = useState<string>(getDefaultStartDate());
  const [endDate, setEndDate] = useState<string>(formatDate(new Date()));

  // Topic specific dates
  const [topicStartDate, setTopicStartDate] = useState<string>(getDefaultStartDate());
  const [topicEndDate, setTopicEndDate] = useState<string>(formatDate(new Date()));
  const [topic, setTopic] = useState<string>('');
  const [searchTopic, setSearchTopic] = useState<string>('');

  const fetchKnowledge = useQuery({
    enabled: !!session?.access_token,
    queryKey: ['GET', 'knowledge', id, session?.access_token, startDate, endDate],
    queryFn: async () =>
      // Return dummy data instead of making API call
      dummyGeneralKnowledge,
  });

  const fetchTopicKnowledge = useQuery({
    enabled: !!session?.access_token && !!searchTopic,
    queryKey: [
      'GET',
      'knowledge',
      'topic',
      id,
      session?.access_token,
      searchTopic,
      topicStartDate,
      topicEndDate,
    ],
    queryFn: async () => {
      // Return dummy data based on topic
      const topicKey = searchTopic.toLowerCase() as keyof typeof dummyTopicKnowledge;
      return dummyTopicKnowledge[topicKey] || 'No specific knowledge found for this topic.';
    },
  });

  const handleSearch = () => {
    setSearchTopic(topic);
  };

  const renderContent = (query: typeof fetchKnowledge) => {
    if (query.isLoading) {
      return (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography
          variant="body1"
          component="pre"
          sx={{
            whiteSpace: 'pre-wrap',
            fontFamily: 'inherit',
            color: 'secondary.darker',
            fontSize: '1rem',
            lineHeight: 1.6,
            '& > *': {
              marginBottom: '0.5rem',
            },
            '& > *:last-child': {
              marginBottom: 0,
            },
          }}
        >
          {query?.data?.toString() ?? ''}
        </Typography>
      </Box>
    );
  };

  const isLoading = fetchKnowledge.isLoading || fetchTopicKnowledge.isLoading;

  return (
    <MuiPageWrapper>
      <Stack spacing={3}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: { xs: 2, sm: 3 },
            backgroundColor: 'background.paper',
            mt: 2,
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h6">General Knowledge</Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                backgroundColor: 'background.paper',
              }}
            >
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    size="small"
                    disabled={isLoading}
                  />
                  <TextField
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    size="small"
                    disabled={isLoading}
                  />
                </Stack>
                <Divider />
                <Box sx={{ mt: -2 }}>{renderContent(fetchKnowledge)}</Box>
              </Stack>
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: { xs: 2, sm: 3 },
            backgroundColor: 'background.paper',
            mt: 2,
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h6">Topic-Specific Knowledge</Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                backgroundColor: 'background.paper',
              }}
            >
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                  <Stack direction="row" spacing={1} sx={{ minWidth: 100 }}>
                    <TextField
                      label="Start Date"
                      type="date"
                      value={topicStartDate}
                      onChange={e => setTopicStartDate(e.target.value)}
                      size="small"
                      disabled={isLoading}
                    />
                    <TextField
                      label="End Date"
                      type="date"
                      value={topicEndDate}
                      onChange={e => setTopicEndDate(e.target.value)}
                      inputProps={{
                        min: topicStartDate,
                        max: addDays(topicStartDate, 90),
                      }}
                      size="small"
                      disabled={isLoading}
                    />
                  </Stack>
                  <TextField
                    label="Enter Topic"
                    value={topic}
                    onChange={e => setTopic(e.target.value)}
                    placeholder="e.g., maintenance, rent, parking"
                    size="small"
                    disabled={isLoading}
                    sx={{ flex: 1, maxWidth: 500 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={isLoading || !topic}
                    sx={{
                      minWidth: 100,
                      mt: '22px',
                      height: '38px',
                    }}
                  >
                    Search
                  </Button>
                </Stack>
                {searchTopic && (
                  <>
                    <Divider />
                    <Box sx={{ mt: -2 }}>{renderContent(fetchTopicKnowledge)}</Box>
                  </>
                )}
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </MuiPageWrapper>
  );
};

export default PropertyLocationViewKnowledgeSubpage;
